import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, merge } from 'lodash';
import generateTooltipDecoratorFunc from 'generate-tooltip-decorator';
import generateYAxisDecoratorFunc from 'generate-y-axis-decorator';
import applyColumnFormatFunc from 'apply-column-format';
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExtraLabelFormat from 'highcharts-extra-label-format';
import Accessibility from 'highcharts/modules/accessibility';
import { UI_COLOR_BRAND_PRIMARY, UI_COLOR_DEFAULT} from 'envy-ui';
import { chartContextButtonInit } from './chartSettings';

require('highcharts-exporting')(Highcharts);
require('highcharts-export-csv')(Highcharts);
require('highcharts-no-data-to-display')(Highcharts);

HighchartsMore(Highcharts);
HighchartsExtraLabelFormat(Highcharts);
Accessibility(Highcharts);
chartContextButtonInit();

export default function PmChart({ showCSVDownload = true, height = 200, config, onChartRef }) {
  const highcharts = useRef(null);

  useEffect(() => {
    if (onChartRef) {
      onChartRef(highcharts.current);
    }
  }, [onChartRef]);

  let isOldStructure = true;
  let currentConfig = JSON.parse(config);

  if (currentConfig.highChartSettings) {
    isOldStructure = false;
    currentConfig = currentConfig.highChartSettings;
  }

  currentConfig = merge(currentConfig, {
    chart: {
      height,
      // A11y hack for internal Highcharts context button — no longer needed
      // since the button is now rendered externally as a native React <button>.
      // events: {
      //   load() {
      //     if (btn && btn.element) {
      //       btn.element.setAttribute("tabindex", "0");
      //       btn.element.setAttribute("role", "button");
      //       btn.element.setAttribute("aria-label", "Chart menu");
      //
      //       btn.element.addEventListener("keydown", (e) => {
      //         if (e.key === "Enter" || e.key === " ") {
      //           e.preventDefault();
      //           btn.element.dispatchEvent(new MouseEvent("click", { bubbles: true }));
      //         }
      //       });
      //     }
      //   }
      // }
    },
    title: {
      text: currentConfig.title?.text || currentConfig.exporting?.filename || 'Chart',
      style: { display: 'none' },
    },
    legend: showCSVDownload && currentConfig.legend ? currentConfig.legend : false,
    credits: false,
    navigation: {
      ...(currentConfig.legend
        ? {
            buttonOptions: {
              enabled: showCSVDownload,
              align: 'right',
              x: -50,
            },
          }
        : {}),
    },
    exporting: {
      ...(currentConfig.exporting
        ? {
            enabled: showCSVDownload || false,
            csv: {
              columnHeaderFormatter: null,
              dateFormat: '%Y-%m-%d %H:%M:%S',
              decimalPoint: null,
              itemDelimiter: null,
              lineDelimiter: '\n',
            },
            buttons: {
              contextButton: {
                // Internal Highcharts context button disabled — CSV download
                // is now handled by an external React button for accessibility.
                // menuItems: [
                //   {
                //     textKey: 'downloadCSV',
                //     onclick: () => highcharts.current.chart.downloadCSV(),
                //   },
                // ],
                // ...contextButtonVisualSettings,
                enabled: false,
              },
            },
          }
        : { enabled: false }),
    },
    accessibility: {
      enabled: true,
      description: currentConfig.exporting?.filename || currentConfig.title?.text || 'Chart',
      keyboardNavigation: {
        enabled: true,
        order: ['series', 'zoom', 'rangeSelector', 'legend'],
        focusBorder: {
          enabled: true,
          style: {
            color: UI_COLOR_BRAND_PRIMARY,
            lineWidth: 2,
            borderRadius: 3,
          },
        },
        wrapAround: true,
      },
      screenReaderSection: {
        beforeChartFormat: '<div>{typeDescription}</div><div>{chartLongdesc}</div>',
      },
      series: {
        describeSingleSeries: true,
      },
    },
    lang: {
      ...(currentConfig.exporting
        ? {
            contextButtonTitle: 'Download CSV',
          }
        : {}),
    },
    noData: {
      style: {
        fontWeight: 'bold',
        fontSize: '20px',
        color: UI_COLOR_DEFAULT,
      },
    },
  });

  const chartType = currentConfig.chart.type;

  if (isOldStructure) {
    if (chartType === 'pie') {
      const tooltipFormatter = generateTooltipDecoratorFunc({
        shared: false,
        chartType: 'pie',
      });

      currentConfig.tooltip = { formatter: tooltipFormatter };
    } else {
      const tooltipFormatter = generateTooltipDecoratorFunc({
        shared: false,
        chartType: 'column',
      });
      const yAxisFormatter = generateYAxisDecoratorFunc(applyColumnFormatFunc);

      currentConfig.tooltip = { formatter: tooltipFormatter };

      let newYAxis = [];

      if (currentConfig.yAxis && currentConfig.yAxis.length > 0) {
        newYAxis = currentConfig.yAxis.map((axis) => ({
          ...axis,
          labels: { formatter: yAxisFormatter },
        }));
      }

      currentConfig.yAxis = newYAxis;
    }
  }

  return <HighchartsReact highcharts={Highcharts} options={currentConfig} ref={highcharts} />;
}

PmChart.propTypes = {
  config: PropTypes.string.isRequired,
  showCSVDownload: PropTypes.bool,
  height: PropTypes.number,
  onChartRef: PropTypes.func,
};
